import { ReactNode } from 'react';

import { UserProfileDefaultFields } from 'api';
import { DEFAULT_DATE_FORMAT } from 'constants/dateFormats';
import { useSubscription } from 'hooks';
import i18n from 'i18n';
import { UserProfileField, UserExtendedDetails, UserStatus } from 'types';
import { formattedDate, getFullName, toCapitalize } from 'utils';

import { ActivationStatusValueW, StatusButton, StatusText } from './styled';

interface ExtendedDetailsAdapterReturn {
  publicInfo: Record<string, { label: string; value: string }>;
  activationStatus: Record<string, { label: string; value: ReactNode }>;
  privateInformation: Record<string, { label: string; value: string }>;
  emergencyContact: Record<string, { label: string; value: string }>;
}

interface ExtendedDetailsAdapterTypes {
  user: UserExtendedDetails;
  onHandleOpenChangeStatusModal: () => void;
  isManager: boolean;
  isCurrentUser: boolean;
  locales?: Locale | undefined;
  userProfileFields?: UserProfileField[];
}

interface UserFieldMapping {
  [key: string]: string;
}

interface DataMapping {
  [key: string]: { label: string; value: string };
}
interface PublicInfo {
  [key: string]: {
    label: string;
    value: string;
  };
}

export const UserFieldMapping: UserFieldMapping = {
  [UserProfileDefaultFields.NAME]: 'name',
  [UserProfileDefaultFields.EMAIL]: 'email',
  [UserProfileDefaultFields.PHONE]: 'phone',
  [UserProfileDefaultFields.BIRTHDATE]: 'birthDate',
  [UserProfileDefaultFields.DEPARTMENT]: 'department',
  [UserProfileDefaultFields.POSITION]: 'position',
  [UserProfileDefaultFields.ADDRESS1]: 'address1',
  [UserProfileDefaultFields.ADDRESS2]: 'address2',
  [UserProfileDefaultFields.CITY]: 'city',
  [UserProfileDefaultFields.REGION]: 'region',
  [UserProfileDefaultFields.POSTCODE]: 'postCode',
  [UserProfileDefaultFields.COUNTRY]: 'country',
  [UserProfileDefaultFields.SSN]: 'ssn',
  [UserProfileDefaultFields.EMPLOYMENTDATE]: 'employmentDate',
  [UserProfileDefaultFields.EMPLOYMENTNUMBER]: 'employmentNumber',
  [UserProfileDefaultFields.EDUCATION]: 'education',
  [UserProfileDefaultFields.SIZEOFCLOTHES]: 'sizeOfClothes',
  [UserProfileDefaultFields.ALLERGIES]: 'allergies',
  [UserProfileDefaultFields.COMMENT]: 'comment',
};

export function ExtendedDetailsAdapter({
  user,
  onHandleOpenChangeStatusModal,
  isManager,
  isCurrentUser,
  locales,
  userProfileFields,
}: ExtendedDetailsAdapterTypes): ExtendedDetailsAdapterReturn {
  const {
    firstName,
    lastName,
    email,
    countryCode,
    phone,
    birthDate,
    department,
    position,
    address1,
    address2,
    city,
    region,
    postcode,
    country,
    status,
    ssn,
    statusComment,
    employedAt,
    employmentNumber,
    education,
    clothesSize,
    allergies,
    comments,
    emergency,
    custom_profile_fields,
  } = user;

  const { isTrial } = useSubscription();

  const dataMapping: DataMapping = {
    name: {
      label: i18n.t('profile.extended.name'),
      value: getFullName({ firstName, lastName }),
    },
    email: { label: i18n.t('profile.edit.form.publicInfo.placeholders.email'), value: email },
    phone: {
      label: i18n.t('profile.edit.form.publicInfo.placeholders.phone'),
      value: countryCode && phone ? `+${countryCode} ${phone}` : '',
    },
    birthDate: {
      label: i18n.t('profile.edit.form.publicInfo.placeholders.birthDate'),
      value: birthDate ? formattedDate(new Date(birthDate), DEFAULT_DATE_FORMAT, locales) : '',
    },
    department: {
      label: i18n.t('profile.edit.form.publicInfo.placeholders.companyDep'),
      value: department ? toCapitalize(department) : '',
    },
    position: {
      label: i18n.t('profile.edit.form.publicInfo.placeholders.companyPos'),
      value: position ? toCapitalize(position) : '',
    },
    address1: {
      label: i18n.t('profile.edit.form.publicInfo.placeholders.address1'),
      value: address1,
    },
    address2: {
      label: i18n.t('profile.edit.form.publicInfo.placeholders.address2'),
      value: address2,
    },
    city: { label: i18n.t('profile.edit.form.publicInfo.placeholders.city'), value: city },
    region: { label: i18n.t('profile.edit.form.publicInfo.placeholders.state'), value: region },
    postCode: {
      label: i18n.t('profile.edit.form.publicInfo.placeholders.postcode'),
      value: postcode,
    },
    country: { label: i18n.t('profile.edit.form.publicInfo.placeholders.country'), value: country },
    ssn: { label: i18n.t('profile.edit.form.privateInfo.placeholders.ssn'), value: ssn },
    employmentDate: {
      label: i18n.t('profile.edit.form.privateInfo.placeholders.employmentDate'),
      value: employedAt ? formattedDate(new Date(employedAt), DEFAULT_DATE_FORMAT, locales) : '',
    },
    employmentNumber: {
      label: i18n.t('profile.edit.form.privateInfo.placeholders.employmentNumber'),
      value: employmentNumber,
    },
    education: {
      label: i18n.t('profile.edit.form.privateInfo.placeholders.education'),
      value: education,
    },
    sizeOfClothes: {
      label: i18n.t('profile.edit.form.privateInfo.placeholders.sizeOfClothes'),
      value: clothesSize ? clothesSize.toUpperCase() : '',
    },
    allergies: {
      label: i18n.t('profile.edit.form.privateInfo.placeholders.allergies'),
      value: allergies ? toCapitalize(allergies) : '',
    },
    comment: {
      label: i18n.t('profile.edit.form.privateInfo.placeholders.comment'),
      value: comments,
    },
  };

  const publicInfo: PublicInfo = {};
  const privateInfo: PublicInfo = {};

  userProfileFields?.map((field) => {
    const isCustom = !field.defaults;
    const category = field.category;
    const label = field.label;

    if (isCustom) {
      if (category === 'public') {
        publicInfo[label] = {
          label: label,
          value: custom_profile_fields?.[label] || '',
        };
      } else {
        privateInfo[label] = {
          label: label,
          value: custom_profile_fields?.[label] || '',
        };
      }
    }
    const key = UserFieldMapping[label];
    if (key) {
      if (category === 'public') {
        publicInfo[key] = dataMapping[key];
      } else {
        privateInfo[key] = dataMapping[key];
      }
    }
  });

  const activateButtonText = i18n.t(
    status === UserStatus.Inactive
      ? 'profile.extended.activationStatus.activate'
      : 'profile.extended.activationStatus.offboard',
  );

  const activationStatus = {
    status: {
      label: i18n.t('profile.extended.activationStatus.title'),
      value: (
        <ActivationStatusValueW>
          <StatusText className={status === UserStatus.Inactive ? 'warning' : 'info'}>
            {toCapitalize(status)}
          </StatusText>
          {isManager && !isCurrentUser && !isTrial && (
            <StatusButton
              variant="contained"
              sx={{
                maxWidth: 'none',
                marginTop: '6px',
              }}
              onClick={() => {
                onHandleOpenChangeStatusModal();
              }}
            >
              {activateButtonText}
            </StatusButton>
          )}
        </ActivationStatusValueW>
      ),
    },
    comment: { label: i18n.t('profile.extended.activationStatus.comment'), value: statusComment },
  };

  const emergencyCountryCode = emergency?.countryCode ? `+${emergency.countryCode} ` : '';
  const emergencyContact = {
    relation: {
      label: i18n.t('profile.edit.form.emergency.placeholders.relation'),
      value: emergency?.relation
        ? toCapitalize(i18n.t(`employee.contact.${emergency.relation}`))
        : '',
    },
    name: {
      label: i18n.t('profile.extended.name'),
      value: getFullName({ firstName: emergency?.firstName, lastName: emergency?.lastName }),
    },
    phone: {
      label: i18n.t('profile.edit.form.publicInfo.placeholders.phone'),
      value: `${emergencyCountryCode}${emergency?.phone || ''}`,
    },
  };

  return {
    publicInfo,
    activationStatus,
    privateInformation: privateInfo,
    emergencyContact,
  };
}
